import { useQuery } from '@tanstack/react-query'
import { collectionService } from '@/services/products'
import { useAuthStore } from '@/stores/auth'

export const collectionKeys = {
  all: (orgId?: string) => ['collections', orgId] as const,
  lists: (orgId?: string) => [...collectionKeys.all(orgId), 'list'] as const,
  detail: (id: string, orgId?: string) =>
    [...collectionKeys.all(orgId), 'detail', id] as const,
  stats: (id: string, orgId?: string) =>
    [...collectionKeys.detail(id, orgId), 'stats'] as const,
  chatLinks: (id: string, orgId?: string) =>
    [...collectionKeys.detail(id, orgId), 'chatLinks'] as const,
  pdpQaResolvability: (id: string, orgId?: string) =>
    [...collectionKeys.detail(id, orgId), 'pdpQaResolvability'] as const,
}

interface UseCollectionsOptions {
  enabled?: boolean
}

export function useCollections(options: UseCollectionsOptions = {}) {
  const { organization } = useAuthStore()
  const { enabled = true } = options

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: collectionKeys.lists(organization?.id),
    queryFn: async () => {
      const response = await collectionService.getCollections()
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch collections')
      }
      return response.collections
    },
    enabled: enabled && !!organization?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  const collections = data ?? []

  return {
    collections,
    isLoading,
    isFetching,
    error,
    refetch,
  }
}
